import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import api from "../../lib/api";
import { AlertCircle, ExternalLink, BookOpen } from "lucide-react";

/**
 * AI/SEO Readiness dashboard. NO arbitrary "score" — just an actionable
 * issue list with severity, category, and direct admin links.
 */
const SEVERITY_STYLES = {
  critical: "bg-rose-50 text-rose-800 border-rose-200",
  high:     "bg-orange-50 text-orange-800 border-orange-200",
  medium:   "bg-amber-50 text-amber-900 border-amber-200",
  low:      "bg-blue-50 text-blue-800 border-blue-200",
};

export function ReadinessDashboard() {
  const [data, setData] = useState(null);
  const [sevFilter, setSevFilter] = useState("all");
  const [catFilter, setCatFilter] = useState("all");

  useEffect(() => {
    api.get("/admin/readiness")
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(e.response?.data?.detail || "Failed to load readiness report"));
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.issues.filter((i) =>
      (sevFilter === "all" || i.severity === sevFilter) &&
      (catFilter === "all" || i.category === catFilter)
    );
  }, [data, sevFilter, catFilter]);

  if (!data) return <div className="text-sm text-stone-500">Loading readiness report…</div>;

  return (
    <div data-testid="readiness-dashboard" className="space-y-5">
      <div className="vv-card p-6 grid md:grid-cols-4 gap-6">
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500">Total issues</div>
          <div className="font-display text-3xl mt-1" data-testid="readiness-total">{data.total_issues}</div>
          <div className="text-xs text-stone-500 mt-1">across {data.published_pages} published pages ({data.total_pages} total)</div>
        </div>
        <div>
          <div className="text-xs uppercase tracking-widest text-stone-500">By severity</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {["critical","high","medium","low"].map((s) => (
              <button key={s} onClick={() => setSevFilter(sevFilter === s ? "all" : s)}
                data-testid={`sev-filter-${s}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border ${SEVERITY_STYLES[s]} ${sevFilter === s ? "ring-2 ring-offset-1 ring-stone-800" : ""}`}>
                {s}: {data.by_severity[s] || 0}
              </button>
            ))}
          </div>
        </div>
        <div className="md:col-span-2">
          <div className="text-xs uppercase tracking-widest text-stone-500">By category</div>
          <div className="mt-2 flex flex-wrap gap-2">
            {Object.entries(data.by_category).map(([c, n]) => (
              <button key={c} onClick={() => setCatFilter(catFilter === c ? "all" : c)}
                data-testid={`cat-filter-${c}`}
                className={`text-[11px] font-semibold px-2.5 py-1 rounded-full border bg-stone-100 text-stone-700 border-stone-200 ${catFilter === c ? "ring-2 ring-offset-1 ring-stone-800" : ""}`}>
                {c}: {n}
              </button>
            ))}
          </div>
        </div>
      </div>

      <div className="text-xs text-stone-500">Showing {filtered.length} of {data.total_issues} issues.</div>

      {/* Orphaned references — active refs with used_by_count == 0 --------- */}
      {data.orphaned_references ? (
        <div
          className="vv-card p-5 border-l-4 border-amber-400 bg-amber-50/40"
          data-testid="readiness-orphan-refs-card"
        >
          <div className="flex items-start gap-3">
            <div className="w-9 h-9 rounded-full bg-amber-100 border border-amber-200 grid place-items-center text-amber-800 flex-shrink-0">
              <BookOpen size={16} />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-3 flex-wrap">
                <div className="font-display text-lg text-stone-900">
                  Orphaned references:{" "}
                  <span data-testid="readiness-orphan-count">
                    {data.orphaned_references.count}
                  </span>
                </div>
                <Link
                  to={data.orphaned_references.drill_down_link || "/admin/references?usage=orphaned"}
                  className="text-xs text-[color:var(--vv-teal)] hover:underline inline-flex items-center gap-1"
                  data-testid="readiness-orphan-drilldown"
                >
                  Review orphans <ExternalLink size={11} />
                </Link>
              </div>
              <p className="text-xs text-stone-600 mt-1">
                Active (non-deprecated) references not currently cited by any content page.
                Nothing is auto-deprecated — this is an editorial signal only. References
                explicitly reserved for future drafts (verification_status contains "reserved"
                or note starts with "RESERVED") are excluded.
              </p>
              {(data.orphaned_references.samples || []).length ? (
                <div className="mt-3 overflow-x-auto">
                  <table className="w-full text-xs" data-testid="readiness-orphan-samples-table">
                    <thead className="text-stone-500">
                      <tr>
                        <th className="text-left font-medium py-1 pr-3">Title</th>
                        <th className="text-left font-medium py-1 pr-3">Year</th>
                        <th className="text-left font-medium py-1 pr-3">Journal</th>
                        <th className="text-left font-medium py-1 pr-3">Verification</th>
                        <th className="text-left font-medium py-1 pr-3">Created</th>
                        <th className="text-left font-medium py-1 pr-3">Last used</th>
                        <th className="text-left font-medium py-1">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.orphaned_references.samples.map((r) => (
                        <tr key={r.id} className="border-t border-amber-100" data-testid={`readiness-orphan-${r.id}`}>
                          <td className="py-1.5 pr-3 text-stone-800 max-w-[24rem] truncate" title={r.title}>{r.title || "(untitled)"}</td>
                          <td className="py-1.5 pr-3 text-stone-600">{r.year || "—"}</td>
                          <td className="py-1.5 pr-3 text-stone-600 max-w-[10rem] truncate" title={r.journal}>{r.journal || "—"}</td>
                          <td className="py-1.5 pr-3 text-stone-600">{(r.verification_status || "").replace(/_/g, " ") || "—"}</td>
                          <td className="py-1.5 pr-3 text-stone-500">{(r.created_at || "").slice(0, 10) || "—"}</td>
                          <td className="py-1.5 pr-3 text-stone-500">{(r.last_used_at || "").slice(0, 10) || "—"}</td>
                          <td className="py-1.5">
                            <Link
                              to={`/admin/references?usage=orphaned`}
                              className="text-[color:var(--vv-teal)] hover:underline"
                            >
                              Review
                            </Link>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {data.orphaned_references.count > data.orphaned_references.samples.length ? (
                    <div className="text-[11px] text-stone-500 mt-2">
                      +{data.orphaned_references.count - data.orphaned_references.samples.length} more —
                      <Link to="/admin/references?usage=orphaned" className="ml-1 underline hover:text-stone-800">see all in library</Link>
                    </div>
                  ) : null}
                </div>
              ) : (
                <div className="text-xs text-stone-600 mt-2" data-testid="readiness-orphan-none">
                  No orphaned references — every active citation is referenced by at least one page.
                </div>
              )}
            </div>
          </div>
        </div>
      ) : null}

      <div className="vv-card overflow-hidden">
        <table className="w-full text-sm">
          <thead className="bg-stone-50 border-b border-stone-200">
            <tr>
              {["Severity","Category","Issue","Page","Actions"].map((h) => (
                <th key={h} className="text-left px-4 py-2 text-xs uppercase tracking-widest text-stone-500">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {filtered.length === 0 && (
              <tr><td colSpan={5} className="text-center text-stone-500 py-8">No issues match those filters.</td></tr>
            )}
            {filtered.map((i, idx) => (
              <tr key={`${i.slug}-${idx}`} className="border-b border-stone-100" data-testid={`issue-row-${idx}`}>
                <td className="px-4 py-2">
                  <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full border ${SEVERITY_STYLES[i.severity]}`}>{i.severity}</span>
                </td>
                <td className="px-4 py-2 text-xs text-stone-600">{i.category}</td>
                <td className="px-4 py-2 text-stone-800">{i.message}</td>
                <td className="px-4 py-2 text-xs text-stone-500">
                  <div className="font-medium text-stone-800">{i.title}</div>
                  <div>{i.page_type}/{i.slug}</div>
                </td>
                <td className="px-4 py-2 whitespace-nowrap">
                  <Link to={i.admin_link} target="_blank" className="text-xs text-[color:var(--vv-gold-deep)] hover:underline inline-flex items-center gap-1">
                    Fix <ExternalLink size={10} />
                  </Link>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
